import React from "react";
import { type GetStaticProps, type NextPage } from "next";
import { Flex, Text } from "@chakra-ui/react";
import { Page } from "components/page";
import { Section } from "components/section";
import { Container } from "ui/container";
import { StackedLogos } from "components/logos";
import { HeroGradient } from "components/pricing/hero-gradient";
import { PricingCards } from "components/pricing/cards";
import { PricingTables } from "components/pricing/tables";
import { Faq } from "components/pricing/faq";
import { BackgroundGradient } from "components/pricing/quote-gradient";
import { CenteredQuote } from "components/quotes";
import {
  getPage,
  getAnnouncementBanner,
  getStackedLogos,
  type Customer,
  type Page as SanityPage,
  type Banner,
} from "lib/sanity";
import { getPageMetaImage } from "utils";

type PageProps = {
  page: SanityPage;
  announcementBanner?: Banner;
  customerRows: Customer[][];
};

const Pricing: NextPage<Readonly<PageProps>> = ({ page, announcementBanner, customerRows }) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <Section center fullWidth pos="relative" my={0} pt={{ base: 12, md: 24 }} overflowX="hidden">
        <HeroGradient />
        <Container px={0} zIndex={1}>
          <Text textStyle="uppercase" fontSize={{ base: "md", md: "lg" }} textAlign="center">
            {page.eyebrow}
          </Text>

          <Text as="h1" textStyle="h3" textAlign="center" mt={3}>
            {page.title}
          </Text>

          <PricingCards />
        </Container>
      </Section>

      <StackedLogos customerRows={customerRows} mb={{ base: 0, md: 16, lg: 32 }} />

      <PricingTables />

      <Section bg="white" overflowX="hidden">
        <Container pos="relative" px={0}>
          <BackgroundGradient />

          <Flex
            align="center"
            bg="white"
            border="1px"
            borderColor="new.gray.150"
            borderRadius="base"
            boxShadow="base"
            direction="column"
            justify="center"
            py={{ base: 5, sm: 8, lg: 24 }}
            px={{ base: 6, sm: 10 }}
            pos="relative"
            zIndex={1}
          >
            <CenteredQuote
              quoteText="One of the big value props of Hightouch is time-to-value. We’ve seen a lot of value very quickly."
              author={{ name: "Natalie Miles", job: "Head of Marketing Technology and Personalization" }}
              company={{
                name: "Chime",
                logo: "https://cdn.sanity.io/images/pwmfmi47/production/0bfab275e65467db361389702dbb62cb2f01b24c-1106x316.png",
                logoRatio: 5 / 1,
              }}
            />
          </Flex>
        </Container>
      </Section>

      <Faq />
    </Page>
  );
};

export default Pricing;

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, announcementBanner, customerRows] = await Promise.all([
    getPage("/pricing"),
    getAnnouncementBanner(),
    getStackedLogos(),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      announcementBanner,
      customerRows,
    },
    revalidate: 60,
  };
};
